import React, { useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';

const Settings = () => {
  const [theme, setTheme] = useState('light');
  const [accentColor, setAccentColor] = useState('#7C3AED');
  const [fontSize, setFontSize] = useState('medium');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [notificationSound, setNotificationSound] = useState('soft');
  const [twoFactorAuth, setTwoFactorAuth] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [language, setLanguage] = useState('english');
  const [timezone, setTimezone] = useState('utc');

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Settings
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Settings
        </Typography>

        <Grid container spacing={3}>
          {/* Appearance Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Appearance
                </Typography>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Theme
                  </Typography>
                  <ToggleButtonGroup
                    value={theme}
                    exclusive
                    onChange={(e, newTheme) => newTheme && setTheme(newTheme)}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value="light">Light</ToggleButton>
                    <ToggleButton value="dark">Dark</ToggleButton>
                    <ToggleButton value="system">System</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Accent Color
                  </Typography>
                  <TextField
                    fullWidth
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Font Size
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={fontSize} onChange={(e) => setFontSize(e.target.value)}>
                      <MenuItem value="small">Small</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="large">Large</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Notifications
                </Typography>

                <Box mb={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email Notification
                    </Typography>
                    <Switch
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {emailNotifications ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Push Notification
                    </Typography>
                    <Switch
                      checked={pushNotifications}
                      onChange={(e) => setPushNotifications(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {pushNotifications ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Notification Sound
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={notificationSound} onChange={(e) => setNotificationSound(e.target.value)}>
                      <MenuItem value="soft">Soft</MenuItem>
                      <MenuItem value="loud">Loud</MenuItem>
                      <MenuItem value="silent">Silent</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Security
                </Typography>

                <Box mb={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Two-Factor Authentication
                    </Typography>
                    <Switch
                      checked={twoFactorAuth}
                      onChange={(e) => setTwoFactorAuth(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {twoFactorAuth ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Session Timeout (Minutes)
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)}>
                      <MenuItem value="15">15 minutes</MenuItem>
                      <MenuItem value="30">30 minutes</MenuItem>
                      <MenuItem value="60">1 hour</MenuItem>
                      <MenuItem value="120">2 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Login History
                  </Typography>
                  <Button variant="outlined" size="small" fullWidth>
                    History
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Language & Region Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Language & Region
                </Typography>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Language
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                      <MenuItem value="english">English</MenuItem>
                      <MenuItem value="spanish">Spanish</MenuItem>
                      <MenuItem value="french">French</MenuItem>
                      <MenuItem value="german">German</MenuItem>
                      <MenuItem value="japanese">Japanese</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Timezone
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                      <MenuItem value="utc">UTC</MenuItem>
                      <MenuItem value="est">EST (UTC-5)</MenuItem>
                      <MenuItem value="pst">PST (UTC-8)</MenuItem>
                      <MenuItem value="cet">CET (UTC+1)</MenuItem>
                      <MenuItem value="jst">JST (UTC+9)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" size="large">
            Reset to Default
          </Button>
          <Button variant="contained" size="large">
            Save Changes
          </Button>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default Settings;
